import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CalendarDays, Eye, FileText, Globe2, type LucideIcon } from 'lucide-react';
import { useHistory, useDeleteHistory, useUpdateHistoryStatus } from '@/lib/api/hooks/history';
import { ActionDropdown, DeleteModal, PublishModal, UnpublishModal, type DropdownAction } from '@/components/ui';
import { showToast } from '@/lib/toast';

const HistoryDetailPage: React.FC = () => {
    const { historyId } = useParams<{ historyId: string }>();
    const navigate = useNavigate();
    const safeHistoryId = historyId || '';

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const { data: historyData, isLoading, error } = useHistory(safeHistoryId);
    const history = historyData?.data;

    const { mutate: deleteHistory, isPending: isDeleting } = useDeleteHistory();
    const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateHistoryStatus();

    if (!historyId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">History ID missing</h2>
                <button onClick={() => navigate('/admin/history')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to History
                </button>
            </div>
        );
    }

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const formatTime = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return 'N/A';
        }
    };

    const getInitials = (name: string) => {
        const parts = name.trim().split(' ').filter(Boolean);
        if (!parts.length) return 'A';
        if (parts.length === 1) return parts[0][0].toUpperCase();
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    };

    const handleEdit = () => {
        navigate(`/admin/history/edit/${historyId}`, {
            state: { history }
        });
    };

    const handleDeleteClick = () => {
        setDeleteModalOpen(true);
    };

    const handleStatusUpdateClick = () => {
        if (!history) return;
        setStatusModalOpen(true);
    };

    const confirmDelete = () => {
        if (!history) return;

        const deleteData = {
            fileUrl: history.imageUrl,
            year: history.year,
            title: history.title,
            description: history.description
        };

        deleteHistory(
            { historyId, data: deleteData },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success('History deleted successfully!');
                        setDeleteModalOpen(false);
                        navigate('/admin/history');
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const confirmStatusUpdate = () => {
        if (!history) return;
        const newStatus = history.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

        updateStatus(
            { historyId, data: { status: newStatus } },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success(
                            newStatus === 'PUBLISH'
                                ? 'History published successfully!'
                                : 'History unpublished successfully!'
                        );
                        setStatusModalOpen(false);
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const getDropdownActions = (): DropdownAction[] => {
        const actions: DropdownAction[] = [
            {
                label: 'Edit',
                onClick: (e) => {
                    e.stopPropagation();
                    handleEdit();
                }
            },
            {
                label: history?.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
                onClick: (e) => {
                    e.stopPropagation();
                    handleStatusUpdateClick();
                },
                disabled: isUpdatingStatus
            }
        ];

        if (history?.status === 'DRAFT') {
            actions.push({
                label: 'Delete',
                onClick: (e) => {
                    e.stopPropagation();
                    handleDeleteClick();
                },
                variant: 'danger' as const,
                disabled: isDeleting
            });
        }

        return actions;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#681628] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading history...</p>
                </div>
            </div>
        );
    }

    if (error || !history) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold text-red-600">Error loading history</h2>
                <p className="mt-2 text-gray-600">{error?.message || 'History not found'}</p>
                <button onClick={() => navigate('/admin/history')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to History
                </button>
            </div>
        );
    }

    const historyMeta = history as unknown as {
        createdAt?: string | null;
        updatedAt?: string | null;
        createdByName?: string | null;
        publishedByName?: string | null;
        totalViews?: number | null;
    };

    const authorName = historyMeta.publishedByName || historyMeta.createdByName || history.createdByUserName || 'Admin';
    const createdByName = historyMeta.createdByName || history.createdByUserName || 'Admin';
    const createdAt = historyMeta.createdAt || history.datePublished;
    const updatedAt = historyMeta.updatedAt || history.datePublished;
    const publishedAt = history.datePublished || historyMeta.createdAt;
    const totalViews = historyMeta.totalViews ?? 0;
    const statusLabel = history.status === 'PUBLISH' ? 'Published' : 'Draft';
    const statusClasses = history.status === 'PUBLISH'
        ? 'bg-emerald-100 text-emerald-700'
        : 'bg-blue-100 text-blue-700';

    const statsItems: Array<{
        label: string;
        value: string | number;
        icon: LucideIcon;
        iconClassName: string;
        valueClassName?: string;
    }> = [
            {
                label: 'Views',
                value: totalViews,
                icon: Eye,
                iconClassName: 'bg-[#04785710] text-emerald-700'
            },
            {
                label: 'Published',
                value: formatDate(publishedAt),
                icon: CalendarDays,
                iconClassName: 'bg-[#f9577b10] text-rose-700',
            },
            {
                label: 'Content Type',
                value: 'History',
                icon: FileText,
                iconClassName: 'bg-[#fbbf2410] text-amber-700'
            },
            {
                label: 'Visibility',
                value: 'Public',
                icon: Globe2,
                iconClassName: 'bg-[#6366f110] text-indigo-700'
            }
        ];

    return (
        <>
            <div className="space-y-6">
                <div className="flex items-center gap-2 text-sm">
                    <Link to="/admin/history" className="text-forestGreen-100 hover:underline font-medium">History</Link>
                    <span className="text-gray-400">▸</span>
                    <span className="text-gray-600">Details</span>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 shadow-sm">
                    <div className="flex flex-col lg:grid lg:grid-cols-[7fr_5fr] 2xl:grid-cols-[8fr_4fr] 2xl:gap-12 gap-4 lg:gap-6">
                            <div className="flex flex-col md:grid md:grid-cols-[auto_1fr] md:items-start gap-5">
                                <div className="w-full h-full md:w-60  rounded overflow-hidden bg-gray-100 border border-gray-200 shrink-0">
                                    {history.imageUrl ? (
                                        <img
                                            src={history.imageUrl}
                                            alt={history.title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                                            <FileText className="w-10 h-10" />
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold bg-wine-100 text-white">
                                                {history.year}
                                            </span>
                                            <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold ${statusClasses}`}>
                                                {statusLabel}
                                            </span>
                                        </div>
                                        <ActionDropdown
                                            id={`history-${history.historyId}`}
                                            actions={getDropdownActions()}
                                        />
                                    </div>

                                    <h1 className="text-2xl font-semibold text-gray-900  break-words">
                                        {history.title}
                                    </h1>

                                   

                                    <div className="mt-2 flex flex-col 2xl:flex-row 2xl:items-center gap-4 2xl:gap-8">
                                        <div className="items-center grid grid-cols-[2rem_1fr] gap-4">
                                            <span className="h-8 w-8 rounded-full bg-wine-100 text-white inline-flex items-center justify-center font-semibold shrink-0">
                                                {getInitials(authorName)}
                                            </span>
                                            <div className="min-w-0">
                                                <p className="text-gray-900 font-medium">Posted by {authorName}</p>
                                              
                                            </div>
                                        </div>

                                        <div className="items-center grid grid-cols-[2rem_1fr] gap-4 text-gray-700">
                                       
                                                <CalendarDays className="w-8 h-8" strokeWidth={1} />
                                            <div>
                                                <p className="text font-medium text-gray-900">Published on {formatDate(publishedAt)}</p>
                                                <p className="text-sm text-gray-500">{formatTime(publishedAt)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        <div className="border border-gray-200 rounded-xl p-4 overflow-hidden bg-white">
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                {statsItems.map((item) => {
                                    const Icon = item.icon;
                                 

                                    return (
                                        <div
                                            key={item.label}
                                            className={`p-4 border border-gray-200 rounded-md `}
                                        >
                                            <div className="grid items-start gap-2 2xl:grid-cols-[auto_1fr] 2xl:gap-4">
                                                <span className={`h-10 w-10 rounded inline-flex items-center justify-center ${item.iconClassName}`}>
                                                    <Icon className="w-6 h-6" />
                                                </span>
                                                <div>
                                                    <p className="text-base text-gray-500">{item.label}</p>
                                                    <p className={`font-semibold text-gray-900 ${item.valueClassName || 'text-lg'}`}>{item.value}</p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                    <div className="xl:col-span-7 2xl:col-span-6 space-y-6">
                        {history.description && (
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
                                <p className="text-gray-700  whitespace-pre-line break-words">{history.description}</p>
                            </div>
                        )}

                        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Story Details</h2>
                            <div className="divide-y divide-gray-200">
                                <div className="flex items-center justify-between gap-4 py-3">
                                    <span className="text-gray-500 inline-flex items-center gap-2">
                                        <CalendarDays className="w-4 h-4" />
                                        Created
                                    </span>
                                    <span className="text-gray-900 font-medium text-right">{formatDate(createdAt)} at {formatTime(createdAt)}</span>
                                </div>

                                <div className="flex items-center justify-between gap-4 py-3">
                                    <span className="text-gray-500 inline-flex items-center gap-2">
                                        <CalendarDays className="w-4 h-4" />
                                        Last Updated
                                    </span>
                                    <span className="text-gray-900 font-medium text-right">{formatDate(updatedAt)} at {formatTime(updatedAt)}</span>
                                </div>

                                <div className="flex items-center justify-between gap-4 py-3">
                                    <span className="text-gray-500 inline-flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        Created By
                                    </span>
                                    <span className="text-gray-900 font-medium text-right">{createdByName}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="xl:col-span-5 2xl:col-span-6">
                        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">Image</h2>
                            {history.imageUrl ? (
                                <img
                                    src={history.imageUrl}
                                    alt={history.title}
                                    className="w-full max-h-[540px] object-cover rounded-xl"
                                />
                            ) : (
                                <div className="w-full h-72 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400">
                                    <FileText className="w-10 h-10" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete History"
                subtitle="Are you sure you want to delete this history?"
                warningText="This action cannot be undone."
                isLoading={isDeleting}
                itemType="History"
            />

            {/* Publish/Unpublish Modals */}
            {history.status === 'DRAFT' ? (
                <PublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    title="Publish History"
                    subtitle="Are you sure you want to publish this history?"
                    itemType="History"
                    isLoading={isUpdatingStatus}
                />
            ) : (
                <UnpublishModal
                    isOpen={statusModalOpen}
                    onClose={() => setStatusModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    itemType="History"
                    isLoading={isUpdatingStatus}
                />
            )}
        </>
    );
};

export default HistoryDetailPage;
